import { Field } from '@herit-fe/heritage-ui';
import { getKstToday } from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';
import { DateField } from './date-field';
import { FieldLabel } from './field-label';
import { MonthField } from './month-field';
import { PERIOD_FILTER_MAX_RANGE_DAYS, shiftDate } from './period-filter';
import type { PeriodType } from './period-math';
import { SelectField, type SelectOption, YEAR_OPTIONS } from './select-field';

const UNIT_LABEL: Record<PeriodType, string> = {
  day: UI_TEXT.TERM_DAY,
  month: UI_TEXT.TERM_MONTH,
  year: UI_TEXT.TERM_YEAR,
  range: UI_TEXT.TERM_PERIOD,
};

export interface PeriodFilterControlsProps {
  period: PeriodType;
  date: string;
  startDate: string;
  endDate: string;
  setPeriod: (period: PeriodType) => void;
  setDate: (date: string) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  /** 노출 날짜기준 — 발전량=[day,month,year,range], 수익=[month,year,range]. */
  periods: PeriodType[];
  /** Field id prefix(a11y) — 호출처 useId. */
  idPrefix: string;
}

/**
 * 조회조건 기간 컨트롤(발전량·수익 공용) — **무상태**(상태=Applied, live). admin 조회 룩: **날짜기준 select** +
 * 값 필드 — 일=조회일자·월=조회월·연=조회연도(단일), 기간=시작일+종료일(역전·최대 span=입력 min/max + 훅
 * clampPeriodRange). `Field` 세트만 렌더(래핑·인접 필드는 호출처 controls 가 소유).
 */
export function PeriodFilterControls({
  period,
  date,
  startDate,
  endDate,
  setPeriod,
  setDate,
  setStartDate,
  setEndDate,
  periods,
  idPrefix,
}: PeriodFilterControlsProps) {
  const today = getKstToday();
  const fid = (key: string) => `${idPrefix}-${key}`;
  const dateTypeOptions: SelectOption[] = periods.map((p) => ({ value: p, label: UNIT_LABEL[p] }));

  return (
    <>
      {/* 날짜기준. */}
      <Field>
        <FieldLabel htmlFor={fid('dateType')}>{UI_TEXT.PERIOD_FILTER_DATE_TYPE}</FieldLabel>
        <SelectField
          id={fid('dateType')}
          label={UI_TEXT.PERIOD_FILTER_DATE_TYPE}
          options={dateTypeOptions}
          value={period}
          onChange={(v) => setPeriod(v as PeriodType)}
        />
      </Field>

      {/* 값 필드 — 일/월/연=단일 기준값, 기간=시작일+종료일(역전·최대 span min/max). */}
      {period === 'range' ? (
        <>
          <Field>
            <FieldLabel htmlFor={fid('startDate')}>{UI_TEXT.TERM_START_DATE}</FieldLabel>
            <DateField
              id={fid('startDate')}
              label={UI_TEXT.TERM_START_DATE}
              value={startDate}
              min={shiftDate(endDate, -PERIOD_FILTER_MAX_RANGE_DAYS)}
              max={endDate}
              onChange={setStartDate}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={fid('endDate')}>{UI_TEXT.TERM_END_DATE}</FieldLabel>
            <DateField
              id={fid('endDate')}
              label={UI_TEXT.TERM_END_DATE}
              value={endDate}
              min={startDate}
              max={(() => {
                const cap = shiftDate(startDate, PERIOD_FILTER_MAX_RANGE_DAYS);
                return cap < today ? cap : today;
              })()}
              onChange={setEndDate}
            />
          </Field>
        </>
      ) : (
        <Field>
          <FieldLabel htmlFor={fid('date')}>{UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}</FieldLabel>
          {period === 'day' ? (
            <DateField
              id={fid('date')}
              label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
              value={date}
              max={today}
              onChange={setDate}
            />
          ) : period === 'month' ? (
            <MonthField
              id={fid('date')}
              label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
              value={date.slice(0, 7)}
              max={today.slice(0, 7)}
              onChange={(ym) => setDate(`${ym}-01`)}
            />
          ) : (
            <SelectField
              id={fid('date')}
              label={UI_TEXT.PERIOD_FILTER_DATE_DIM(period)}
              options={YEAR_OPTIONS}
              value={date.slice(0, 4)}
              onChange={(year) => setDate(`${year}-01-01`)}
            />
          )}
        </Field>
      )}
    </>
  );
}
